import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Phone, MessageCircle, Award, BadgeCheck, FileCheck, Shield, Clock, Star, CheckCircle } from "lucide-react";

const UeberUnsPage = () => {
  const qualifications = [
    { icon: Award, title: "Kfz-Meister", desc: "Ausgebildeter Kfz-Meister mit jahrelanger Erfahrung" },
    { icon: BadgeCheck, title: "Zertifizierter Gutachter", desc: "Unabhängiger und zertifizierter Kfz-Sachverständiger" },
    { icon: FileCheck, title: "Diplom-Zertifikate", desc: "Zertifizierter Kfz-Sachverständiger" },
    { icon: Shield, title: "Unabhängig", desc: "Keine Bindung an Versicherungen oder Werkstätten" }
  ];

  const values = [
    { title: "Unabhängigkeit", desc: "Wir arbeiten ausschließlich in Ihrem Interesse – nicht im Interesse einer Versicherung." },
    { title: "Schnelligkeit", desc: "Innerhalb von 60 Minuten sind wir bei Ihnen. Das Gutachten erhalten Sie in 24 Stunden." },
    { title: "Fairness", desc: "Wir dokumentieren alle Schäden vollständig, damit Sie die Entschädigung erhalten, die Ihnen zusteht." },
    { title: "Transparenz", desc: "Wir erklären Ihnen jeden Schritt und halten Sie über den Stand der Abwicklung auf dem Laufenden." }
  ];

  return (
    <>
      <SEO 
        title="Über uns - Bo-Ingmar Schmeißer"
        description="Bo-Ingmar Schmeißer - Ihr unabhängiger Kfz-Sachverständiger. Kfz-Meister, zertifizierter Gutachter. Erfahrung, Kompetenz und Unabhängigkeit für faire Entschädigung."
        canonical="/ueber-uns"
        keywords="Bo-Ingmar Schmeißer, Kfz Gutachter, Sachverständiger, Kfz-Meister, zertifiziert"
      />
      {/* Hero */}
      <section className="bg-[#F7F7F7] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Über uns</span>
              <h1 className="text-4xl sm:text-5xl font-black text-black mb-6 font-['Outfit']">
                Bo-Ingmar Schmeißer
              </h1>
              <p className="text-xl text-[#4A4A4A] mb-4 font-medium">Ihr unabhängiger Kfz-Sachverständiger</p>
              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-8">
                Als unabhängiger Kfz-Gutachter stehe ich für objektive und professionelle Gutachten. Mit meiner Erfahrung als Kfz-Meister und zertifizierter Sachverständiger helfe ich Ihnen, Ihre Ansprüche nach einem Unfall vollständig durchzusetzen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
                  <Phone className="w-5 h-5" /><span>0174 893 6514</span>
                </a>
                <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="border-2 border-black text-black px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-black hover:text-white transition-colors">
                  <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white p-4 rounded-sm shadow-lg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/4aee379d-d7a9-43ea-b5e1-4f4900c746b3/images/d7e67a2e521e11a5c3ce3d08a6ad142f684ef2397570e3b16ba9342115f03a44.png" 
                  alt="Bo-Ingmar Schmeißer - Kfz Gutachter" 
                  className="w-full h-auto rounded-sm"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#AA6622] text-white p-6 rounded-sm shadow-lg">
                <div className="flex items-center space-x-1 mb-1">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-white" />)}
                </div>
                <div className="text-2xl font-black font-['Outfit']">222+</div>
                <div className="text-sm">zufriedene Kunden</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">Qualifikationen & Zertifizierungen</h2>
            <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">
              Professionelle Ausbildung und kontinuierliche Weiterbildung garantieren höchste Qualität in der Begutachtung.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualifications.map((q, i) => (
              <div key={i} className="bg-[#F7F7F7] p-6 rounded-sm text-center card-hover">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <q.icon className="w-8 h-8 text-[#AA6622]" />
                </div>
                <h3 className="font-bold text-black mb-2 font-['Outfit']">{q.title}</h3>
                <p className="text-sm text-[#4A4A4A]">{q.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">Meine Mission</h2>
          </div>
          <div className="bg-white p-8 md:p-12 rounded-sm shadow-lg">
            <p className="text-lg text-[#4A4A4A] leading-relaxed mb-6">
              Ein Unfall ist eine Stresssituation. Neben dem Schock und den organisatorischen Herausforderungen müssen Sie sich auch noch mit Versicherungen auseinandersetzen. Genau hier komme ich ins Spiel.
            </p>
            <p className="text-lg text-[#4A4A4A] leading-relaxed mb-6">
              Als unabhängiger Gutachter bin ich auf Ihrer Seite. Ich dokumentiere den Schaden objektiv und vollständig – nicht so, wie es eine Versicherung gerne hätte, sondern so, wie er tatsächlich ist.
            </p>
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Mein Ziel ist es, dass Sie die Entschädigung erhalten, die Ihnen zusteht. Nicht mehr, aber auch nicht weniger.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Outfit']">Meine Werte</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((v, i) => (
              <div key={i} className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-[#AA6622] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-black mb-2 font-['Outfit'] text-xl">{v.title}</h3>
                  <p className="text-[#4A4A4A] leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Availability */}
      <section className="py-20 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Clock className="w-16 h-16 text-[#AA6622] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Erreichbarkeit</h2>
          <p className="text-lg text-[#4A4A4A] mb-8">
            Ein Unfall passiert nicht nur zu den üblichen Geschäftszeiten. Deshalb bin ich täglich von <strong>9:00 bis 23:00 Uhr</strong> für Sie erreichbar – auch am Wochenende und an Feiertagen.
          </p>
          <div className="bg-white p-6 rounded-sm inline-block mb-8">
            <div className="text-2xl font-bold text-black font-['Outfit']">Mo – So: 09:00 – 23:00 Uhr</div>
            <div className="text-[#AA6622] font-medium">Innerhalb 60 Minuten vor Ort</div>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
              <Phone className="w-5 h-5" /><span>0174 893 6514</span>
            </a>
            <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#20bd5a] transition-colors">
              <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Haben Sie Fragen?</h2>
          <p className="text-lg text-[#4A4A4A] mb-8">
            Ich berate Sie gerne unverbindlich zu Ihrem Anliegen. Kontaktieren Sie mich telefonisch, per WhatsApp oder über das Kontaktformular.
          </p>
          <Link to="/kontakt" className="inline-flex items-center space-x-2 border-2 border-black text-black px-8 py-4 rounded-sm font-bold hover:bg-black hover:text-white transition-colors">
            <span>Zum Kontaktformular</span>
          </Link>
        </div>
      </section>
    </>
  );
};

export default UeberUnsPage;
